import { cva, type VariantProps } from "class-variance-authority"
import * as React from "react"

import { cn } from "@/lib/utils"
import { semanticTokens } from "@packages/config/theme"

const alertVariants = cva(
  "relative w-full rounded-lg border p-4 [&>svg~*]:pl-7 [&>svg+div]:translate-y-[-3px] [&>svg]:absolute [&>svg]:left-4 [&>svg]:top-4 [&>svg]:text-foreground",
  {
    variants: {
      variant: {
        default: "bg-background text-foreground",
        destructive:
          "border-destructive/50 text-destructive dark:border-destructive [&>svg]:text-destructive",
        warning: cn(
          semanticTokens.warning.bg,
          semanticTokens.warning.border,
          semanticTokens.warning.fg,
          // Use semantic token for icon color (already includes [&>svg] selector)
          semanticTokens.warning.icon,
        ),
        info: cn(
          semanticTokens.info.bg,
          semanticTokens.info.border,
          semanticTokens.info.fg,
          "[&>svg]:text-blue-600"
        ),
        success: cn(
          semanticTokens.positive.bg,
          semanticTokens.positive.border,
          semanticTokens.positive.fg,
          "[&>svg]:text-green-600"
        ),
        premium: cn(
          semanticTokens.premium.bg,
          semanticTokens.premium.border,
          semanticTokens.premium.fg,
          "[&>svg]:text-slate-300"
        ),
        zen: cn(
          semanticTokens.zen.bg,
          semanticTokens.zen.border,
          semanticTokens.zen.fg,
          "[&>svg]:text-[#132B24]"
        ),
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

const Alert = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & VariantProps<typeof alertVariants>
>(({ className, variant, ...props }, ref) => (
  <div
    ref={ref}
    role="alert"
    className={cn(alertVariants({ variant }), className)}
    {...props}
  />
))
Alert.displayName = "Alert"

const AlertTitle = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h5
    ref={ref}
    className={cn("mb-1 font-medium leading-none tracking-tight", className)}
    {...props}
  />
))
AlertTitle.displayName = "AlertTitle"

const AlertDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("text-sm [&_p]:leading-relaxed", className)}
    {...props}
  />
))
AlertDescription.displayName = "AlertDescription"

export { Alert, AlertDescription, AlertTitle }
